/* eslint-disable @typescript-eslint/no-magic-numbers */
import { BasketAddressModel } from 'models/Basket';
import { CardSuccessfulPaymentResponseModel, PaypalSuccessfulPaymentResponseModel } from './Payment';

export class OrderPayload {
    public payments: OrderPayment[];
    public useFinance: boolean;

    constructor(billingAddress: BasketAddressModel, isFinanceFlow: boolean, middleName: string | null) {
        this.useFinance = isFinanceFlow;
        this.payments = [{
            paymentInfo: this.createBasePaymentInfo(billingAddress, middleName),
            paymentTransactions: [],
            requestId: null,
            requestToken: null
        }];
    }

    public fillOrderPayloadForCardPayment(paymentResult: CardSuccessfulPaymentResponseModel, deposit: number) {
        this.payments[0].paymentInfo.type = 'Card';
        this.payments[0].paymentTransactions = [this.createCardOrderPaymentTransaction(paymentResult, deposit)];
        this.payments[0].paymentInfo.cardNumber = '**** **** **** 0001';
    }

    public fillOrderPayloadForPaypalPayment(paymentResult: PaypalSuccessfulPaymentResponseModel, deposit: number) {
        this.payments[0].paymentInfo.type = 'Paypal';
        this.payments[0].paymentInfo.paymentId = paymentResult.id;
        this.payments[0].paymentInfo.payerId = paymentResult.payerId;
        this.payments[0].paymentInfo.emailAddress = paymentResult.payerEmail;
        this.payments[0].paymentInfo.id = paymentResult.invoiceNumber;

        this.payments[0].paymentTransactions = [this.createPaypalOrderPaymentTransaction(paymentResult, deposit)];

        this.payments[0].requestId = paymentResult.invoiceNumber;
        this.payments[0].requestToken = paymentResult.id;
    }

    private createBasePaymentInfo(billingAddress: BasketAddressModel, middleName: string | null): OrderPaymentInfo {
        return {
            type: '',
            accountHolderName: `${billingAddress.firstName} ${billingAddress.surname}` || '',
            billingAddress: this.createBillingAddress(billingAddress, middleName),
            cardNumber: null,
            cardType: null,
            cardTypeData: {
                code: null,
                name: null,
            },
            defaultPaymentInfo: true,
            expiryMonth: null,
            expiryYear: null,
            id: null,
            issueNumber: '',
            saved: true,
            startMonth: '',
            startYear: '',
            subscriptionId: '',
            paymentId: null,
            payerId: null,
            emailAddress: null
        };
    }

    private createBillingAddress(billingAddress: BasketAddressModel, middleName: string | null): OrderBillingAddress {
        return {
            apartament: billingAddress.apartment!,
            billingAddress: false,
            building: billingAddress.building!,
            cellPhone: billingAddress.phone!,
            companyName: '',
            contactAddress: true,
            country: {
                isocode: 'GB',
                name: 'United Kingdom',
            },
            dateOfBirth: '',
            defaultAddress: false,
            department: '',
            district: billingAddress.district!,
            email: billingAddress.email!,
            fax: '',
            firstName: billingAddress.firstName!,
            formattedAddress: billingAddress.formattedAddress!,
            gender: '',
            id: billingAddress.id!,
            lastName: billingAddress.surname!,
            line1: `${billingAddress.apartment} ${billingAddress.building} ${billingAddress.line1}`,
            line2: billingAddress.town!,
            line3: '',
            middlename: middleName ?? '',
            middlename2: '',
            phone: billingAddress.phone!,
            phone2: '',
            pobox: '',
            postalCode: billingAddress.postcode.code,
            region: {
                countryIso: 'GB',
                isocode: 'GB',
                isocodeShort: 'GB',
            },
            remarks: '',
            shippingAddress: false,
            title: billingAddress.title!,
            titleCode: billingAddress.title!,
            town: billingAddress.town!,
            unloadingAddress: true,
            url: '',
            visibleInAddressBook: true,
        };
    }

    private createCardOrderPaymentTransaction(paymentResult: CardSuccessfulPaymentResponseModel, deposit: number): OrderPaymentTransaction {
        return {
            currency: 'GBP',
            paymentProvider: 'Adyen',
            requestId: paymentResult.vendorTxCode,
            paymentTransactionEntries: [{
                type: 'CAPTURE',
                amount: deposit,
                currency: 'GBP',
                time: this.dateToHybrisFormat(new Date()),
                transactionStatus: 'Authorised',
                transactionStatusDetails: '',
                requestId: '',
                requestToken: '',
                subscriptionId: '',
                version: ''
            }]
        };
    }

    private createPaypalOrderPaymentTransaction(paymentResult: PaypalSuccessfulPaymentResponseModel, deposit: number): OrderPaymentTransaction {
        return {
            currency: 'GBP',
            paymentProvider: 'PayPal',
            requestId: null,
            paymentTransactionEntries: [{
                type: 'CAPTURE',
                amount: deposit,
                currency: 'GBP',
                time: this.dateToHybrisFormat(new Date()),
                transactionStatus: paymentResult.state,
                transactionStatusDetails: paymentResult.state,
                requestId: paymentResult.invoiceNumber,
                requestToken: paymentResult.id,
                subscriptionId: '',
                version: 'v1'
            }]
        };
    }

    private dateToHybrisFormat(date: Date): string {
        let day = this.leftFillNum(date.getDate());
        let month = this.leftFillNum(date.getMonth() + 1);
        let hour = this.leftFillNum(date.getHours());
        let minute = this.leftFillNum(date.getMinutes());
        let seconds = this.leftFillNum(date.getSeconds());

        return day + '/' + month + '/' + date.getFullYear() + ' ' + hour + ':' + minute + ':' + seconds;
    }

    private leftFillNum(digit: number): string {
        return digit < 10 ? '0' + digit.toString() : digit.toString();
    }
}

interface OrderPayment {
    paymentInfo: OrderPaymentInfo;
    paymentTransactions: OrderPaymentTransaction[];
    requestId: string | null;
    requestToken: string | null;
}

interface OrderPaymentInfo {
    type: string;
    accountHolderName: string;
    billingAddress: OrderBillingAddress;
    cardNumber: string | null;
    cardType: string | null;
    cardTypeData: {
        code: string | null;
        name: string | null;
    };
    expiryMonth: string | null;
    expiryYear: string | null;
    defaultPaymentInfo: boolean;
    id: string | null;
    issueNumber: string;
    saved: boolean;
    startMonth: string;
    startYear: string;
    subscriptionId: string;
    paymentId: string | null;
    payerId: string | null;
    emailAddress: string | null;
}

interface OrderPaymentTransaction {
    currency: string;
    paymentProvider: string;
    requestId: string | null;
    paymentTransactionEntries: {
        type: string;
        amount: number;
        currency: string;
        time: string;
        transactionStatus: string;
        transactionStatusDetails: string;
        requestId: string;
        requestToken: string;
        subscriptionId: string;
        version: string;
    }[];
}

interface OrderBillingAddress {
    apartament: string;
    billingAddress: boolean;
    building: string;
    cellPhone: string;
    companyName: string;
    contactAddress: boolean;
    country: {
        isocode: string;
        name: string;
    };
    dateOfBirth: string;
    defaultAddress: boolean;
    department: string;
    district: string;
    email: string;
    fax: string;
    firstName: string;
    formattedAddress: string;
    gender: string;
    id: string;
    lastName: string;
    line1: string;
    line2: string;
    line3: string;
    middlename: string;
    middlename2: string;
    phone: string;
    phone2: string;
    pobox: string;
    postalCode: string;
    region: {
        countryIso: string;
        isocode: string;
        isocodeShort: string;
    };
    remarks: string;
    shippingAddress: boolean;
    title: string;
    titleCode: string;
    town: string;
    unloadingAddress: boolean;
    url: string;
    visibleInAddressBook: boolean;
}